import {
  Close as CloseIcon,
  AccountBalance as AccountIcon,
  TrendingUp as TrendingUpIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  IconButton,
  Divider,
} from '@mui/material';
import React, { useState, useEffect } from 'react';

const ONBOARDING_STEPS = [
  {
    title: '👋 StackStock에 오신 것을 환영합니다!',
    content: '개인 주식 포트폴리오를 효율적으로 관리할 수 있는 완전한 솔루션입니다.',
    details: [
      '실시간 주가 정보와 포트폴리오 분석',
      '거래 내역 추적 및 수익률 계산',
      '시각적 차트와 상세한 분석 리포트',
    ],
    icon: <DashboardIcon sx={{ fontSize: 60, color: 'primary.main' }} />,
  },
  {
    title: '💼 계좌를 생성해보세요',
    content: '투자 계좌를 생성하여 거래 내역을 관리할 수 있습니다.',
    details: [
      '여러 개의 투자 계좌 분리 관리',
      '계좌별 포트폴리오 분석',
      '입출금 및 거래 내역 추적',
    ],
    icon: <AccountIcon sx={{ fontSize: 60, color: 'success.main' }} />,
    actionText: '계좌 페이지로 이동',
    actionPath: '/accounts',
  },
  {
    title: '📈 첫 거래를 입력해보세요',
    content: '입금, 매수, 매도, 배당 등 다양한 거래 유형을 기록할 수 있습니다.',
    details: [
      '간편한 종목 검색 (66개 한국 주식 + ETF)',
      '자동 수익률 계산',
      '실시간 포트폴리오 업데이트',
    ],
    icon: <TrendingUpIcon sx={{ fontSize: 60, color: 'warning.main' }} />,
    actionText: '거래 내역 페이지로 이동',
    actionPath: '/transactions',
  },
  {
    title: '📊 포트폴리오를 분석해보세요',
    content: '배당 흐름과 포트폴리오 트렌드를 한눈에 살펴보세요.',
    details: [
      '계좌·종목별 배당 금액과 원천세 요약',
      '월별 배당 추이와 주요 통계 카드 제공',
      '필터로 연도/계좌/종목을 손쉽게 전환',
    ],
    icon: <AnalyticsIcon sx={{ fontSize: 60, color: 'info.main' }} />,
    actionText: '배당 현황으로 이동',
    actionPath: '/dividends',
  },
  {
    title: '⚙️ 설정을 확인해보세요',
    content: '개인화된 설정으로 더 나은 사용 경험을 만들어보세요.',
    details: [
      '프로필 정보 및 비밀번호 관리',
      '알림 설정 및 테마 선택',
      '데이터 백업 및 보안 설정',
    ],
    icon: <SettingsIcon sx={{ fontSize: 60, color: 'secondary.main' }} />,
    actionText: '설정 페이지로 이동',
    actionPath: '/settings',
  },
];

function OnboardingTour({ open, onClose, onNavigate }) {
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setCompleted(false);
    }
  }, [open]);

  const handleNext = () => {
    if (activeStep < ONBOARDING_STEPS.length - 1) {
      setActiveStep(activeStep + 1);
    } else {
      setCompleted(true);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleSkip = () => {
    // 온보딩 완료 처리
    localStorage.setItem('onboarding_completed', 'true');
    onClose();
  };

  const handleComplete = () => {
    // 온보딩 완료 처리
    localStorage.setItem('onboarding_completed', 'true');
    setCompleted(true);
    setTimeout(() => {
      onClose();
      // 기본 랜딩으로 이동
      if (onNavigate) {
        onNavigate('/accounts');
      }
    }, 1500);
  };

  const handleActionClick = () => {
    const currentStep = ONBOARDING_STEPS[activeStep];
    if (currentStep.actionPath && onNavigate) {
      localStorage.setItem('onboarding_completed', 'true');
      onClose();
      onNavigate(currentStep.actionPath);
    }
  };

  const currentStepData = ONBOARDING_STEPS[activeStep];

  return (
    <Dialog
      open={open}
      onClose={handleSkip}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '500px',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
            시작하기 가이드
          </Typography>
          <IconButton onClick={handleSkip} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 1 }}>
        {!completed ? (
          <>
            {/* 단계 표시기 */}
            <Box mb={3}>
              <Stepper activeStep={activeStep} alternativeLabel>
                {ONBOARDING_STEPS.map((step, index) => (
                  <Step key={index}>
                    <StepLabel>
                      <Typography variant="caption" color="text.secondary">
                        Step {index + 1}
                      </Typography>
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>

            {/* 현재 단계 내용 */}
            <Card elevation={0} sx={{ bgcolor: 'background.default', mb: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Box mb={2}>
                  {currentStepData.icon}
                </Box>
                
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  {currentStepData.title}
                </Typography>
                
                <Typography variant="body1" color="text.secondary" paragraph>
                  {currentStepData.content}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Box textAlign="left">
                  {currentStepData.details.map((detail, index) => (
                    <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                      • {detail}
                    </Typography>
                  ))}
                </Box>

                {currentStepData.actionText && (
                  <Box mt={2}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleActionClick}
                      sx={{ mt: 1 }}
                    >
                      {currentStepData.actionText}
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </>
        ) : (
          // 완료 화면
          <Card elevation={0} sx={{ bgcolor: 'success.light', textAlign: 'center', py: 4 }}>
            <CardContent>
              <Typography variant="h4" sx={{ mb: 2 }}>
                🎉 설정 완료!
              </Typography>
              <Typography variant="h6" color="success.dark" gutterBottom>
                StackStock을 사용할 준비가 되었습니다
              </Typography>
              <Typography variant="body1" color="text.secondary">
                이제 포트폴리오 관리를 시작해보세요!
              </Typography>
            </CardContent>
          </Card>
        )}
      </DialogContent>

      {!completed && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleSkip} color="inherit">
            건너뛰기
          </Button>
          
          <Box flex={1} />
          
          <Button 
            onClick={handleBack} 
            disabled={activeStep === 0}
            sx={{ mr: 1 }}
          >
            이전
          </Button>
          
          {activeStep === ONBOARDING_STEPS.length - 1 ? (
            <Button 
              onClick={handleComplete} 
              variant="contained"
              color="primary"
            >
              시작하기
            </Button>
          ) : (
            <Button 
              onClick={handleNext} 
              variant="contained"
              color="primary"
            >
              다음
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
}

export default OnboardingTour;
